import React, { ReactNode, useEffect, useState, useCallback } from 'react';

import type { Property } from 'csstype';
import PropTypes from 'prop-types';

import { BasicButton } from '@adsk/alloy-react-button';
import { variantSwitch } from '@adsk/alloy-react-helpers';
import {
  AlertInformationIcon,
  AlertWarningIcon,
  CheckmarkCircleIcon,
  XCircleIcon,
  XIcon,
  RenderIconType,
} from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import BannerDivider from './BannerDivider';
import { BannerVariant, BANNER_VARIANTS } from './consts';

type BannerProps = StylableComponent<HTMLDivElement> & {
  renderIcon?: RenderIconType;
  variant?: BannerVariant;
  onClose?: () => void;
  message?: ReactNode;
  open?: boolean;
  inline?: boolean;
};

export const mapVariantToIconRenderer = {
  [BANNER_VARIANTS.PRIMARY]: AlertInformationIcon.renderIcon,
  [BANNER_VARIANTS.SUCCESS]: CheckmarkCircleIcon.renderIcon,
  [BANNER_VARIANTS.WARNING]: AlertWarningIcon.renderIcon,
  [BANNER_VARIANTS.ERROR]: XCircleIcon.renderIcon,
};

export const mapVariantToBackgroundColor = {
  [BANNER_VARIANTS.PRIMARY]:
    theme.tokens.colors.background.information.subtle.value,
  [BANNER_VARIANTS.SUCCESS]:
    theme.tokens.colors.background.success.subtle.value,
  [BANNER_VARIANTS.WARNING]:
    theme.tokens.colors.background.warning.subtle.value,
  [BANNER_VARIANTS.ERROR]: theme.tokens.colors.background.error.subtle.value,
};

export const mapVariantToBorderColor = {
  [BANNER_VARIANTS.PRIMARY]: theme.tokens.colors.border.information.value,
  [BANNER_VARIANTS.SUCCESS]: theme.tokens.colors.border.success.value,
  [BANNER_VARIANTS.WARNING]: theme.tokens.colors.border.warning.value,
  [BANNER_VARIANTS.ERROR]: theme.tokens.colors.border.error.value,
};

const VISIBLE_STATE = 'visible';
const HIDDEN_STATE = 'hidden';

/**
 * Banners are notifications alerting an event that requires a user action.
 * They are similar toasts except banners are not sparked by the user’s action,
 * therefore they require a different design.
 * Banners are primarily used for global messages about the system that can be simply informational (blue)
 *  or color coded to signal success (green), warning (yellow) and error (red).
 */
const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  (
    {
      style,
      className,
      renderIcon,
      variant = BANNER_VARIANTS.PRIMARY,
      onClose,
      message,
      open = true,
      inline,
      children,
      ...props
    },
    ref,
  ) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const [visibleState, setVisibleState] = useState<
      Property.Visibility | undefined
    >(VISIBLE_STATE);

    const transitionStart = useCallback(() => {
      if (open) {
        setVisibleState(VISIBLE_STATE);
      }
    }, [open]);

    useEffect(() => {
      document.addEventListener('transitionstart', transitionStart);

      if (open) {
        setVisibleState(VISIBLE_STATE);
      } else {
        setVisibleState(HIDDEN_STATE);
      }

      return () => {
        document.removeEventListener('transitionstart', transitionStart);
      };

      // we only want to react to changes in the open prop on component mounted
      // because subsequent changes of open need to be handled by the transition
      // start and complete handlers to maintain the animation feature of the banner
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (open) {
        setVisibleState(VISIBLE_STATE);
      }
    }, [open]);

    const transitionComplete = useCallback(() => {
      if (!open) {
        setVisibleState(HIDDEN_STATE);
      }
    }, [open]);

    return (
      <div
        {...props}
        ref={ref}
        className={className}
        css={[
          {
            width: '100%',
            display: 'flex',
            visibility: visibleState,
            minHeight: '50px',
            color: theme.tokens.colors.text.primary.value,
            backgroundColor: mapVariantToBackgroundColor[variant],
            transitionDuration: '0.3s',
            transitionProperty: 'transform',
            border: `1px solid ${mapVariantToBorderColor[variant]}`,
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',

            ...(!inline && {
              position: 'absolute',
              top: 0,
              left: 0,
            }),

            ...(inline && {
              borderRadius: theme.borderRadius.medium,
            }),

            ...(open && {
              transform: 'translateY(0)',
            }),

            ...(!open && {
              transform: 'translateY(-100%)',
            }),
          },
          style,
        ]}
        onTransitionEnd={transitionComplete}
      >
        <div
          css={[
            {
              minHeight: 40,
              height: 'auto',
              minWidth: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.tokens.colors.icon.inverse.value,
            },
            variantSwitch({
              statement: variant,
              variants: {
                [BANNER_VARIANTS.PRIMARY]: {
                  backgroundColor:
                    theme.tokens.colors.background.information.value,
                },
                [BANNER_VARIANTS.SUCCESS]: {
                  backgroundColor: theme.tokens.colors.background.success.value,
                },
                [BANNER_VARIANTS.WARNING]: {
                  backgroundColor: theme.tokens.colors.background.warning.value,
                },
                [BANNER_VARIANTS.ERROR]: {
                  backgroundColor: theme.tokens.colors.background.error.value,
                },
              },
            }),
          ]}
        >
          {renderIcon?.({}) || mapVariantToIconRenderer[variant]?.({})}
        </div>
        {message && (
          <p
            css={[
              {
                paddingLeft: 15,
                flex: 1,
              },
            ]}
          >
            {message}
          </p>
        )}
        <div
          css={[
            {
              height: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-evenly',
              flewDirection: 'row',
              paddingLeft: message ? 0 : 15,
              '& button': {
                margin: '0 5px',
              },
            },
          ]}
        >
          {children}
        </div>
        {children && onClose && <BannerDivider variant={variant} />}
        {onClose && (
          <BasicButton
            onClick={onClose}
            style={{
              marginRight: 6,
            }}
            aria-label={T.translate('CLOSE_BANNER')?.toString()}
          >
            <XIcon />
          </BasicButton>
        )}
      </div>
    );
  },
);

Banner.displayName = 'Banner';

Banner.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** render prop to display an icon on the left */
  renderIcon: PropTypes.func,
  /** Banner's variant, defaults to 'primary' */
  variant: PropTypes.oneOf(Object.values(BANNER_VARIANTS)),
  /** Banner's message to display */
  message: PropTypes.any,
  /** Toggles the Banner's visibility */
  open: PropTypes.bool,
  /** A Trigger for the close button in the Banner */
  onClose: PropTypes.func,
  /** Whether or not to use inline Banner (rounded corners, etc.)  */
  inline: PropTypes.bool,
  /** The component's children, here you should put the actions */
  children: PropTypes.any,
};

export default Object.assign(Banner, { VARIANTS: BANNER_VARIANTS });
